import { AppSettingsTypeEnum, QuestionModelEnum, RoleEnum } from "../enums";
import { PaginateQuery } from "../interfaces";
import {
  CreateMediaRequest,
  UploadAndCreateFlashCardRequest,
} from "../interfaces/media";

//#region helper functions
function buildPaginationQueryString(data: PaginateQuery, url: string): string {
  if (data.page) {
    url += `?page=${data.page}`;
  } else {
    url += `?page=${1}`;
  }
  if (data.limit && data?.limit > 0) {
    url += `&limit=${data.limit}`;
  } else {
    url += `&limit=${10}`;
  }
  if (data.sortBy) {
    url += `&sortBy=${data.sortBy}`;
  }
  if (data.search && data.search !== "") {
    url += `&search=${data.search}`;
  }
  if (
    data.filter &&
    Object.keys(data.filter).length &&
    Object.keys(data.filter).length > 0
  ) {
    let filters: string = "";
    const filter = data.filter;
    Object.keys(filter).map((key: string, index: number) => {
      if (filter[key]) filters += `&filter[${key}]=${filter[key]}`;
    });
    url += filters;
  }
  return url;
}

//#endregion

//#region auth
export const AUTH_SIGNIN_URL = "/api/auth/signin";
export const AUTH_SIGNOUT_URL = "/api/auth/signout";
export const AUTH_REFRESH_TOKEN_URL = "/api/auth/refresh-token";
export const AUTH_FORGOT_PASSWORD_URL = "/api/auth/forget-password";
export const AUTH_VERIFY_OTP_URL = "/api/auth/verify";
export const AUTH_RESET_PASSWORD_URL =
  "/api/auth/forget-password/reset-password";
export const USER_CHANGE_PASSWORD_URL = "/api/auth/change-password";
//#endregion

//#region verification
export const VERIFICATION_RESEND_OTP_URL = "/api/verification/resend";
//#endregion

//#region governorates
export const GET_GOVERNORATES_URL = (data: PaginateQuery) =>
  buildPaginationQueryString(data, "/api/governorate");
export const GET_GOVERNORATE_URL = (id: string) => `/api/governorate/${id}`;
export const GET_GOVERNORATE_SELECT_LIST_URL = () =>
  `/api/governorate/select-list/all`;
export const ADD_GOVERNORATE_URL = () => `/api/governorate`;
export const UPDATE_GOVERNORATE_URL = (id: string) => `/api/governorate/${id}`;
export const DELETE_GOVERNORATE_URL = (id: string) => `/api/governorate/${id}`;

//#endregion

//#region cities
export const GET_CITIES_URL = (data: PaginateQuery) =>
  buildPaginationQueryString(data, "/api/city");
export const GET_CITY_URL = (id: string) => `/api/city/${id}`;
export const GET_CITY_SELECT_LIST_URL = (governorateId?: string) => {
  if (governorateId)
    return `/api/city/select-list/all?governorateId=${governorateId}`;
  else return `/api/city/select-list/all`;
};
export const ADD_CITY_URL = () => `/api/city`;
export const UPDATE_CITY_URL = (id: string) => `/api/city/${id}`;
export const DELETE_CITY_URL = (id: string) => `/api/city/${id}`;

//#endregion

//#region schools
export const GET_SCHOOLS_URL = (data: PaginateQuery) =>
  buildPaginationQueryString(data, "/api/school");
export const GET_SCHOOL_URL = (id: string) => `/api/school/${id}`;
export const GET_SCHOOL_SELECT_LIST_URL = (
  governorateId: string,
  cityId: string
) =>
  `/api/school/select-list/all?governorateId=${governorateId}&cityId=${cityId}`;
export const ADD_SCHOOL_URL = () => `/api/school`;
export const UPDATE_SCHOOL_URL = (id: string) => `/api/school/${id}`;
export const DELETE_SCHOOL_URL = (id: string) => `/api/school/${id}`;

//#endregion

//#region courses

export const GET_COURSE_SELECT_LIST_URL = () => `/api/course/select-list/all`;

export const GET_COURSE_AND_GRADES_SELECT_LIST_URL = () =>
  `/api/course/select-list/courses-grades`;

//#endregion

//#region grades

export const GET_GRADES_URL = (data: PaginateQuery) =>
  buildPaginationQueryString(data, "/api/grade");
export const GET_GRADE_URL = (id: string) => `/api/grade/${id}`;
export const GET_GRADE_SELECT_LIST_BY_COURSE_URL = (courseId: string) =>
  `/api/grade/${courseId}/course`;
export const GET_GRADES_DETAIL_BY_COURSE_URL = (courseId: string) =>
  `/api/grade/${courseId}/course/detail`;
export const ADD_GRADE_URL = () => `/api/grade`;
export const UPDATE_GRADE_URL = (id: string) => `/api/grade/${id}`;
export const DELETE_GRADE_URL = (id: string) => `/api/grade/${id}`;

//#endregion

//#region subjects

export const GET_SUBJECTS_URL = (data: PaginateQuery) =>
  buildPaginationQueryString(data, "/api/subject");
export const GET_SUBJECT_URL = (id: string) => `/api/subject/${id}`;
export const GET_SUBJECTS_BY_GRADE_URL = (gradeId: string) =>
  `/api/subject/${gradeId}/grade`;
export const ADD_SUBJECT_URL = () => `/api/subject`;
export const UPDATE_SUBJECT_URL = (id: string) => `/api/subject/${id}`;
export const DELETE_SUBJECT_URL = (id: string) => `/api/subject/${id}`;
export const UPLOAD_SUBJECT_HOME_IMAGE_URL = (subjectId?: string) => {
  if (subjectId) return `/api/subject/upload/home-image?id=${subjectId}`;
  else return `/api/subject/upload/home-image`;
};
export const UPLOAD_SUBJECT_CONTENT_IMAGE_URL = (subjectId?: string) => {
  if (subjectId) return `/api/subject/upload/content-image?id=${subjectId}`;
  else return `/api/subject/upload/content-image`;
};
export const COPY_CUT_SUBJECT = () => `/api/subject/copy-cut`;

export const TOGGLE_SUBJECT_STATE_URL = (id: string) => {
  return `/api/subject/toggle-state/${id}`;
};

export const GET_SUBJECT_SELECT_LIST_BY_GRADE = (gradeId: string) => {
  return `/api/subject/select-list/${gradeId}/grade`;
};

export const GET_PURCHASEABLE_SUBJECTS_FOR_STUDENT = (
  courserId: string,
  gradeId: string,
  userId: string
) => {
  return `/api/subject/purchaseable/${courserId}/${gradeId}/${userId}`;
};

//#endregion

//#region Subject Note

export const GET_SUBJECT_NOTES_URL = (data: PaginateQuery) =>
  buildPaginationQueryString(data, "/api/subject-note");
export const GET_SUBJECT_NOTE_URL = (id: string) => `/api/subject-note/${id}`;
export const ADD_SUBJECT_NOTE_URL = () => `/api/subject-note`;
export const UPDATE_SUBJECT_NOTE_URL = (id: string) =>
  `/api/subject-note/${id}`;
export const DELETE_SUBJECT_NOTE_URL = (id: string) =>
  `/api/subject-note/${id}`;
export const UPLOAD_SUBJECT_NOTE_URL = (id?: string | null) => {
  if (id) return `/api/subject-note/upload?id=${id}`;
  else return `/api/subject-note/upload`;
};
export const DOWNLOAD_SUBJECT_NOTE_URL = (id: string) => {
  return `/api/subject-note/download/${id}`;
};
export const TOGGLE_SUBJECT_NOTE_URL = (noteId: string) => {
  return `/api/subject-note/toggle-state/${noteId}`;
};
//#endregion

//#region chapters
export const GET_CHAPTERS_URL = (data: PaginateQuery) =>
  buildPaginationQueryString(data, "/api/chapter");
export const GET_CHAPTER_URL = (id: string) => `/api/chapter/${id}`;
export const GET_CHAPTERS_STATS_URL = (subjectId?: string) => {
  return `/api/chapter/stats/${subjectId}/subject`;
};
export const GET_CHAPTERS_SKILLS_STATS_URL = (subjectId?: string) => {
  return `/api/chapter/stats/${subjectId}/subject/skills`;
};
export const GET_CHAPTERS_FLASH_CARDS_STATS_URL = (subjectId?: string) => {
  return `/api/chapter/stats/${subjectId}/subject/flash-cards`;
};
export const GET_CHAPTERS_LECTURES_STATS_URL = (subjectId?: string) => {
  return `/api/chapter/stats/${subjectId}/subject/lectures`;
};
export const ADD_CHAPTER_URL = () => `/api/chapter`;
export const UPDATE_CHAPTER_URL = (id: string) => `/api/chapter/${id}`;
export const DELETE_CHAPTER_URL = (id: string) => `/api/chapter/${id}`;

export const TOGGLE_CHAPTER_STATE_URL = (id: string) => {
  return `/api/chapter/toggle-state/${id}`;
};
export const GET_CHAPTER_SELECT_LIST_BY_SUBJECT_URL = (subjectId: string) => {
  return `/api/chapter/select-list/${subjectId}/subject`;
};
export const GET_ORGANIZING_CHAPTERS_URL = (
  subjectId: string,
  data: PaginateQuery
) => buildPaginationQueryString(data, `/api/chapter/list/${subjectId}/subject`);

//#endregion

//#region lessons
export const GET_LESSONS_URL = (data: PaginateQuery) =>
  buildPaginationQueryString(data, "/api/lesson");
export const GET_LESSON_URL = (id: string) => `/api/lesson/${id}`;
export const GET_LESSON_DETAIL_URL = (id: string) => `/api/lesson/${id}/detail`;
export const ADD_LESSON_URL = () => `/api/lesson`;
export const UPDATE_LESSON_URL = (id: string) => `/api/lesson/${id}`;
export const DELETE_LESSON_URL = (id: string) => `/api/lesson/${id}`;
export const TOGGLE_LESSON_STATE_URL = (id: string) => {
  return `/api/lesson/toggle-state/${id}`;
};

export const GET_LESSON_SELECT_LIST_BY_CHAPTER_URL = (chapterId: string) => {
  return `/api/lesson/select-list/${chapterId}/chapter`;
};
//#endregion

//#region questions

export const GET_QUESTION_URL = (id: string) => `/api/question/${id}`;
export const GET_QUESTIONS_BY_LESSON_URL = (
  lessonId: string,
  model?: QuestionModelEnum
) => {
  if (model) {
    return `/api/question/${lessonId}/lesson?model=${model}`;
  } else {
    return `/api/question/${lessonId}/lesson`;
  }
};
export const ADD_QUESTION_URL = () => `/api/question`;
export const UPDATE_QUESTION_URL = (id: string) => `/api/question/${id}`;
export const DELETE_QUESTION_URL = (id: string) => `/api/question/${id}`;
export const UPLOAD_QUESTION_DISPLAY_IMAGE_URL = (questionId?: string) => {
  if (questionId) return `/api/question/upload/display-image?id=${questionId}`;
  else return `/api/question/upload/display-image`;
};
export const TOGGLE_QUESTION_STATE_URL = (id: string) => {
  return `/api/question/toggle-state/${id}`;
};
export const UPLOAD_QUESTION_IMAGE_URL = (tempId: string) => {
  return `/api/question/upload/image/${tempId}`;
};

//#endregion

//#region course durations
export const GET_COURSE_DURATIONS_URL = (
  gradeId: string,
  courseId: string,
  data: PaginateQuery
) =>
  buildPaginationQueryString(
    data,
    `/api/course-duration/${gradeId}/${courseId}`
  );
export const GET_COURSE_DURATION_URL = (id: string) =>
  `/api/course-duration/${id}`;
export const UPDATE_COURSE_DURATION_URL = () => `/api/course-duration`;
export const DELETE_COURSE_DURATION_URL = (id: string) =>
  `/api/course-duration/${id}`;

//#endregion

//#region skills
export const GET_SKILLS_URL = (data: PaginateQuery) =>
  buildPaginationQueryString(data, "/api/skill");
export const GET_SKILLS_BY_LESSON_URL = (lessonId: string) =>
  `/api/skill/${lessonId}/lesson`;
export const GET_SKILLS_BY_SUBJECT_URL = (subjectId: string) =>
  `/api/skill/${subjectId}/subject`;
export const GET_SKILL_URL = (id: string) => `/api/skill/${id}`;
export const ADD_SKILL_URL = () => `/api/skill`;
export const UPDATE_SKILL_URL = (id: string) => `/api/skill/${id}`;
export const DELETE_SKILL_URL = (id: string) => `/api/skill/${id}`;

export const UPLOAD_SKILL_VIDEO_URL = (skillId?: string) => {
  if (skillId) return `/api/skill/upload/video?id=${skillId}`;
  else return `/api/skill/upload/video`;
};

export const GET_SKILLS_SUGGESTION_URL = () => {
  return `/api/skill/suggest-skills`;
};

export const TOGGLE_SKILL_STATE_URL = (id: string) => {
  return `/api/skill/toggle-state/${id}`;
};

export const COPY_SKILLS_URL = `/api/skill/copy`;

//#endregion

//#region flash card
export const GET_FLASH_CARDS_URL = (data: PaginateQuery) =>
  buildPaginationQueryString(data, "/api/flash-card");
export const GET_FLASH_CARDS_BY_LESSON_URL = (lessonId: string) =>
  `/api/flash-card/${lessonId}/lesson`;
export const GET_FLASH_CARD_URL = (id: string) => `/api/flash-card/${id}`;
export const ADD_FLASH_CARD_URL = () => `/api/flash-card`;
export const UPDATE_FLASH_CARD_URL = (id: string) => `/api/flash-card/${id}`;
export const DELETE_FLASH_CARD_URL = (id: string) => `/api/flash-card/${id}`;
export const DELETE_ALL_FLASH_CARD_BY_LESSON_ID_URL = (lessonId: string) =>
  `/api/flash-card/${lessonId}/lesson`;
export const UPLOAD_FLASH_CARD_IMAGE_URL = (flashCardId?: string) => {
  if (flashCardId) return `/api/flash-card/upload/image?id=${flashCardId}`;
  else return `/api/flash-card/upload/image`;
};
export const TOGGLE_FLASH_CARD_STATE_URL = (id: string) => {
  return `/api/flash-card/toggle-state/${id}`;
};

export const UPLOAD_AND_CREATE_FLASH_CARD_URL = (
  request: UploadAndCreateFlashCardRequest
) => {
  return `/api/flash-card/upload-create/${request.lessonId}/${request.sequenceNumber}?localId=${request.localId}`;
};

//#endregion

//#region calendar
export const GET_CALENDAR_EVENTS_URL = (data: PaginateQuery) =>
  buildPaginationQueryString(data, "/api/calendar");
export const GET_ALL_CALENDAR_EVENTS_URL = () => "/api/calendar/all";
export const GET_CALENDAR_EVENT_URL = (id: string) => `/api/calendar/${id}`;
export const ADD_CALENDAR_EVENT_URL = () => `/api/calendar`;
export const UPDATE_CALENDAR_EVENT_URL = (id: string) => `/api/calendar/${id}`;
export const DELETE_CALENDAR_EVENT_URL = (id: string) => `/api/calendar/${id}`;

//#endregion

//#region app settings

export const GET_APP_SETTINGS_URL = (type: AppSettingsTypeEnum) =>
  `/api/app-settings/${type}`;
export const UPDATE_APP_SETTINGS_URL = (type: AppSettingsTypeEnum) =>
  `/api/app-settings/${type}`;

export const GENERATE_SEQUENCE_NUMBER_REQUEST = `/api/app-settings/generate-sequence-number`;

//#endregion

//#region supplications
export const GET_SUPPLICATIONS_URL = (data: PaginateQuery) =>
  buildPaginationQueryString(data, "/api/supplication");
export const GET_SUPPLICATION_URL = (id: string) => `/api/supplication/${id}`;
export const ADD_SUPPLICATION_URL = () => `/api/supplication`;
export const UPDATE_SUPPLICATION_URL = (id: string) =>
  `/api/supplication/${id}`;
export const DELETE_SUPPLICATION_URL = (id: string) =>
  `/api/supplication/${id}`;

//#endregion

//#region banners

export const GET_BANNERS_URL = (data: PaginateQuery) =>
  buildPaginationQueryString(data, "/api/banner");

export const GET_BANNER_URL = (id: string) => `/api/banner/${id}`;
export const ADD_BANNER_URL = () => `/api/banner`;
export const UPDATE_BANNER_URL = (id: string) => `/api/banner/${id}`;
export const DELETE_BANNER_URL = (id: string) => `/api/banner/${id}`;
export const UPLOAD_BANNER_IMAGE_URL = (bannerId?: string) => {
  if (bannerId) return `/api/banner/upload/image?id=${bannerId}`;
  else return `/api/banner/upload/image`;
};
//#endregion

//#region account

export const GET_USER_PROFILE_URL = () => `/api/user/profile/me`;
export const UPDATE_USER_PROFILE_URL = () => `/api/user/profile/me`;
export const UPLOAD_USER_PROFILE_PHOTO_URL = () => `/api/user/profile/photo/me`;
export const CHANGE_PASSWORD_URL = () => `/api/user/profile/change-password`;
//#endregion

//#region teacher admin users
export const GET_TEACHER_ADMIN_USERS_URL = (
  role: RoleEnum,
  data: PaginateQuery
) => buildPaginationQueryString(data, `/api/user/teacher-admin`);
export const GET_TEACHER_ADMIN_USER_URL = (id: string) =>
  `/api/user/teacher-admin/${id}`;
export const ADD_TEACHER_ADMIN_USER_URL = () => `/api/user/teacher-admin`;
export const UPDATE_TEACHER_ADMIN_USER_URL = (id: string) =>
  `/api/user/teacher-admin/${id}`;
export const DELETE_TEACHER_ADMIN_USER_URL = (id: string) => `/api/user/${id}`;
export const TOGGLE_BLOCK_USER = (id: string, state: boolean) =>
  `/api/user/toggle-block/${state}/${id}`;
//#endregion

//#region olympic questions

export const GET_OLYMPIC_QUESTION_URL = (id: string) =>
  `/api/olympic/question/${id}`;
export const GET_QUESTIONS_BY_OLYMPIC_URL = (data: PaginateQuery) => {
  return buildPaginationQueryString(data, `/api/olympic/question`);
};
export const ADD_OLYMPIC_QUESTION_URL = () => `/api/olympic/question`;
export const UPDATE_OLYMPIC_QUESTION_URL = (id: string) =>
  `/api/olympic/question/${id}`;
export const DELETE_OLYMPIC_QUESTION_URL = (id: string) =>
  `/api/olympic/question/${id}`;
export const UPLOAD_OLYMPIC_QUESTION_DISPLAY_IMAGE_URL = (
  questionId?: string
) => {
  if (questionId)
    return `/api/olympic/question/upload/display-image?id=${questionId}`;
  else return `/api/olympic/question/upload/display-image`;
};

export const ADD_UPDATE_OLYMPIC_URL = () => `/api/olympic`;
export const GET_OLYMPIC_URL = (id: string) => `/api/olympic/${id}`;
export const GET_OLYMPICS = (data: PaginateQuery) =>
  buildPaginationQueryString(data, `/api/olympic`);
export const DELETE_OLYMPIC_URL = (id: string) => `/api/olympic/${id}`;
export const GET_OLYMPIC_PARTICIPANTS = (data: PaginateQuery) =>
  buildPaginationQueryString(data, "/api/olympic/participants/results");
//#endregion

//#region promo codes
export const GET_PROMO_CODES_URL = (data: PaginateQuery) =>
  buildPaginationQueryString(data, "/api/promo-code");
export const GET_PROMO_CODE_URL = (id: string) => `/api/promo-code/${id}`;
export const ADD_PROMO_CODE_URL = () => `/api/promo-code`;
export const UPDATE_PROMO_CODE_URL = (id: string) => `/api/promo-code/${id}`;
export const DELETE_PROMO_CODE_URL = (id: string) => `/api/promo-code/${id}`;

//#endregion

//#region plan
export const GET_PLAN = (id: string) => `/api/plan/${id}`;
export const UPDATE_PLAN = (id: string) => `/api/plan/${id}`;
export const ADD_PLAN = () => `/api/plan`;
export const GET_PLANS = (courseId: string, gradeId: string) =>
  `/api/plan?courseId=${courseId}&gradeId=${gradeId}`;
export const UPLOAD_PLAN_IMAGE_URL = (planId?: string) => {
  if (planId) return `/api/plan/upload/image?id=${planId}`;
  else return `/api/plan/upload/image`;
};
export const DELETE_PLAN_URL = (id: string) => `/api/plan/${id}`;
export const TOGGLE_PLAN_STATE_URL = (id: string) => {
  return `/api/plan/toggle-state/${id}`;
};
export const GET_PURCHASEABLE_PLANS_FOR_STUDENT = (
  courserId: string,
  gradeId: string,
  userId: string
) => {
  return `/api/plan/purchasable/${courserId}/${gradeId}/${userId}`;
};
//#endregion

//#region students
export const GET_STUDENTS_URL = (data: PaginateQuery) =>
  buildPaginationQueryString(data, `/api/user/student`);

export const GET_STUDENTS_V2_URL = (data: PaginateQuery) =>
  buildPaginationQueryString(data, `/api/user/student/v2`);
//#endregion

//#region parents
export const GET_PARENTS_URL = (data: PaginateQuery) =>
  buildPaginationQueryString(data, `/api/parent`);
//#endregion

//#region parents
export const GET_TEACHERS_URL = (data: PaginateQuery) =>
  buildPaginationQueryString(data, `/api/user/teacher`);
//#endregion

//#region dashboard
export const GET_ADMIN_ANALYTICS = () => `/api/analytics/main`;
export const GET_ADMIN_ANALYTICS_V2 = () => `/api/analytics/v2/main`;

//#endregion

//#region notification
export const SEND_NOTIFICATION = () => `/api/notification`;
export const SEND_NOTIFICATION_V3 = () => `/api/notification/v3`;
export const GET_ADMIN_SENT_NOTIFICATIONS = (data: PaginateQuery) =>
  buildPaginationQueryString(data, "/api/notification/admin-sent");
export const DELETE_NOTIFICATION_URL = (id: string) =>
  `/api/notification/${id}`;

//#endregion

//#region lectures
export const GET_LECTURES_URL = (data: PaginateQuery) =>
  buildPaginationQueryString(data, "/api/lecture");
export const GET_LECTURES_BY_LESSON_URL = (lessonId: string) =>
  `/api/lecture/${lessonId}/lesson`;
export const GET_LECTURES_BY_SUBJECT_URL = (subjectId: string) =>
  `/api/lecture/${subjectId}/subject`;
export const GET_LECTURE_URL = (id: string) => `/api/lecture/${id}`;
export const ADD_LECTURE_URL = () => `/api/lecture`;
export const UPDATE_LECTURE_URL = (id: string) => `/api/lecture/${id}`;
export const DELETE_LECTURE_URL = (id: string) => `/api/lecture/${id}`;

export const UPLOAD_LECTURE_VIDEO_URL = (lectureId?: string) => {
  if (lectureId) return `/api/lecture/upload/video?id=${lectureId}`;
  else return `/api/lecture/upload/video`;
};
export const TOGGLE_LECTURE_STATE_URL = (id: string) => {
  return `/api/lecture/toggle-state/${id}`;
};
//#endregion

//#region complaints
export const GET_COMPLAINTS_URL = (data: PaginateQuery) =>
  buildPaginationQueryString(data, "/api/complaint");

//#endregion

//#region transaction
export const GET_TRANSACTION_HISTORY_URL = (data: PaginateQuery) =>
  buildPaginationQueryString(data, `/api/transaction/history`);

export const GET_TRANSACTION_HISTORY_V2_URL = (data: PaginateQuery) =>
  buildPaginationQueryString(data, `/api/transaction/history/v2`);
//#endregion

//#region purchase
export const MANUAL_PURCHASE_SUBJECT = () => `/api/purchase/manual`;
//#endregion

//#region reports
export const NO_PURCHASES_STUDENTS_REPORT_URL = () => {
  return `/api/report/no-purchases/students`;
};

export const NO_PURCHASES_PARENTS_REPORT_URL = () => {
  return `/api/report/no-purchases/parents`;
};

export const NO_SUBSCRIPTION_STUDENTS_REPORT_URL = () => {
  return `/api/report/non-subscriber/students`;
};

export const NO_SUBSCRIPTION_PARENTS_REPORT_URL = () => {
  return `/api/report/non-subscriber/parents`;
};
//#endregion

//#region app configs
export const GET_APP_CONFIGS_URL = () => `/api/app-config`;
export const UPDATE_APP_CONFIGS_URL = () => `/api/app-config`;
//#endregion

//#region homework solution
export const GET_HOMEWORK_SOLUTION_QUERIES = () => `/api/homework-solution`;
export const UPDATE_HOMEWORK_SOLUTION_QUERIES = () => `/api/homework-solution`;

//#endregion

export const UPLOAD_MEDIA = (request: CreateMediaRequest) => {
  return `/api/media/${request.type}/${request.subType}?localId=${request.localId}`;
};

//#region media

//#region subscription
export const MANUAL_SUBSCRIPTION_SUBJECT = () => `/api/subscription/manual`;
//#endregion

//#region Template
export const GET_TEMPLATES = () => `/api/template`;
//#endregion
